import './style.modules.scss'

import { BrandIcon, BrandIconColor, BrandIconId } from '@ampcore/adel-react'
import { BackgroundColor } from '@ampcore/ampcom-contracts'
import { AnalyticsTagBuilder } from '@dai-team/analytics-library'
import React, { useMemo } from 'react'
import xss from 'xss'

import { rteContentFilter } from '../../lib/helpers/rteContentFilter'
import { headingColorMap } from '../../main'
import { SpotlightBackgroundColor } from './SpotlightColor.type'
import { SpotlightHeadingColor } from './SpotlightHeadingColor.type'
import { SpotlightVariant } from './SpotlightVariant.type'

export type SpotlightProps = {
  // left
  iconLeft?: BrandIconId
  iconColorLeft?: BrandIconColor
  fillLeft?: boolean
  strokeLeft?: boolean
  headingLeft?: string
  headingLeftColor?: SpotlightHeadingColor
  contentLeft?: string
  linkAnalyticsLeft?: string
  linkUrlLeft?: string
  linkTargetLeft?: string
  linkTextLeft?: string
  bgColorLeft?: SpotlightBackgroundColor
  // right
  iconRight?: BrandIconId
  iconColorRight?: BrandIconColor
  fillRight?: boolean
  strokeRight?: boolean
  headingRight?: string
  headingRightColor?: SpotlightHeadingColor
  contentRight?: string
  linkAnalyticsRight?: string
  linkUrlRight?: string
  linkTargetRight?: string
  linkTextRight?: string
  bgColorRight?: SpotlightBackgroundColor
  variant?: SpotlightVariant
  testId?: string
}

import { addDataAnalyticsToRteLinks } from './addDataAnalyticsToRteLinks'

export const Spotlight = (props: SpotlightProps) => {
  const {
    iconLeft,
    iconColorLeft,
    fillLeft,
    strokeLeft,
    headingLeft,
    headingLeftColor,
    contentLeft,
    linkAnalyticsLeft,
    linkUrlLeft,
    linkTargetLeft,
    linkTextLeft,
    iconRight,
    iconColorRight,
    fillRight,
    strokeRight,
    headingRight,
    headingRightColor,
    contentRight,
    linkAnalyticsRight,
    linkUrlRight,
    linkTargetRight,
    linkTextRight,
    variant,
    bgColorLeft,
    bgColorRight,
    testId
  } = props

  const atb = useMemo(
    () => new AnalyticsTagBuilder().setDefaultName('Spotlight'),
    []
  )

  function getCardColor(bgColor?: SpotlightBackgroundColor) {
    switch (bgColor) {
      case BackgroundColor.MINT_TINT:
        return 'u-bgColorMintLight10'
      case BackgroundColor.PURPLE_TINT:
        return 'u-bgColorPurpleLight10'
      case BackgroundColor.LIME_TINT:
        return 'u-bgColorLimeLight20'
      case BackgroundColor.STONE:
        return 'u-bgColorStone'
      case BackgroundColor.BERRY_TINT:
        return 'u-bgColorBerryLight10'
      case BackgroundColor.ORANGE_TINT:
        return 'u-bgColorOrangeLight20'
      default:
        return 'u-border u-bgColorWhite'
    }
  }

  if (!headingLeft && !contentLeft && !headingRight && !contentRight) {
    return null
  }

  if (variant === SpotlightVariant.SPOTLIGHT_CARD) {
    return (
      <div
        className="Spotlight Spotlight--card u-sizeConstrained u-sm-paddingVert20 u-md-paddingVert20 u-paddingVert32"
        data-testid={testId ? testId : undefined}
      >
        <div className="Grid Grid--withGutter">
          <div className="u-md-size1of2 u-lg-size1of2 u-sm-marginBottom20">
            <div
              className={`Spotlight-individualCard u-sm-paddingVert32 u-sm-paddingHoriz24 u-padding32 ${getCardColor(
                bgColorLeft
              )} u-decoBorder`}
              {...atb.dataAnalyticAttribute('item')}
              data-testid={testId ? `${testId}-card-left` : undefined}
            >
              <div className="Content">
                {iconLeft && (
                  <BrandIcon
                    icon={iconLeft}
                    color={iconColorLeft}
                    fill={fillLeft ? true : false}
                    stroke={strokeLeft ? true : false}
                    testId={testId ? `${testId}-icon-left` : undefined}
                  />
                )}
                {headingLeft && (
                  <div
                    className={`u-paddingVert16 u-textSizeMsrp4 ${
                      headingLeftColor ? headingColorMap[headingLeftColor] : ''
                    }`}
                    dangerouslySetInnerHTML={{ __html: xss(headingLeft) }}
                    data-testid={testId ? `${testId}-heading-left` : null}
                  />
                )}
                {contentLeft && (
                  <div
                    className="u-textSizeMsrp"
                    dangerouslySetInnerHTML={{
                      __html: addDataAnalyticsToRteLinks(
                        rteContentFilter(contentLeft)
                      )
                    }}
                    data-testid={testId ? `${testId}-content-left` : null}
                  />
                )}
              </div>
              {linkTextLeft && (
                <div className="u-paddingTop16">
                  <a
                    className="Button Button--primary"
                    href={`${linkUrlLeft}`}
                    target={linkTargetLeft}
                    {...atb.dataAnalyticAttribute(
                      linkAnalyticsLeft?.trim() || 'item-click'
                    )}
                  >
                    {linkTextLeft}
                  </a>
                </div>
              )}
            </div>
          </div>
          <div className="u-md-size1of2 u-lg-size1of2">
            <div
              className={`Spotlight-individualCard u-sm-paddingVert32 u-sm-paddingHoriz24 u-padding32 ${getCardColor(
                bgColorRight
              )} u-decoBorder`}
              {...atb.dataAnalyticAttribute('item')}
              data-testid={testId ? `${testId}-card-right` : undefined}
            >
              <div className="Content">
                {iconRight && (
                  <BrandIcon
                    icon={iconRight}
                    color={iconColorRight}
                    fill={fillRight ? true : false}
                    stroke={strokeRight ? true : false}
                    testId={testId ? `${testId}-icon-right` : undefined}
                  />
                )}
                {headingRight && (
                  <div
                    className={`u-paddingVert16 u-textSizeMsrp4 ${
                      headingRightColor
                        ? headingColorMap[headingRightColor]
                        : ''
                    }`}
                    dangerouslySetInnerHTML={{ __html: xss(headingRight) }}
                    data-testid={testId ? `${testId}-heading-right` : null}
                  />
                )}
                {contentRight && (
                  <div
                    className="u-textSizeMsrp"
                    dangerouslySetInnerHTML={{
                      __html: addDataAnalyticsToRteLinks(
                        rteContentFilter(contentRight)
                      )
                    }}
                    data-testid={testId ? `${testId}-content-right` : null}
                  />
                )}
              </div>
              {linkTextRight && (
                <div className="u-paddingTop16">
                  <a
                    className="Button Button--primary"
                    href={`${linkUrlRight}`}
                    target={linkTargetRight}
                    {...atb.dataAnalyticAttribute(
                      linkAnalyticsRight?.trim() || 'item-click'
                    )}
                  >
                    {linkTextRight}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Default layout
  return (
    <div
      className="Spotlight u-sizeConstrained u-sm-paddingVert20 u-md-paddingVert20 u-paddingVert32"
      data-testid={testId ? testId : undefined}
    >
      <div className="Grid">
        <div
          className={
            'Spotlight-border u-sm-paddingRight0 u-md-paddingRight48 u-paddingRight48 u-sm-paddingBottom32 u-md-paddingBottom32 u-md-paddingTop32 u-md-size1of2 u-lg-size1of2'
          }
          data-testid={testId ? `${testId}-card-left` : undefined}
        >
          {iconLeft && (
            <BrandIcon
              icon={iconLeft}
              color={iconColorLeft}
              fill={fillLeft ? true : false}
              stroke={strokeLeft ? true : false}
              testId={testId ? `${testId}-icon-left` : undefined}
            />
          )}
          {headingLeft && (
            <div
              className={`u-paddingTop20 u-paddingBottom8 ${
                headingLeftColor ? headingColorMap[headingLeftColor] : ''
              }`}
              dangerouslySetInnerHTML={{ __html: xss(headingLeft) }}
              data-testid={testId ? `${testId}-heading-left` : null}
            />
          )}
          {contentLeft && (
            <div
              className="u-paddingBottom32"
              dangerouslySetInnerHTML={{
                __html: addDataAnalyticsToRteLinks(
                  rteContentFilter(contentLeft)
                )
              }}
              data-testid={testId ? `${testId}-content-left` : null}
            />
          )}
          {linkTextLeft && (
            <a
              className="Button Button--secondary Button--secondary--action-blue"
              href={`${linkUrlLeft}`}
              target={linkTargetLeft}
              {...atb.dataAnalyticAttribute(
                linkAnalyticsLeft?.trim() || 'item-click'
              )}
            >
              {linkTextLeft}
            </a>
          )}
        </div>
        <div
          className={
            'u-sm-paddingLeft0 u-md-paddingLeft48 u-paddingLeft48 u-sm-paddingTop32 u-md-paddingTop32 u-md-size1of2 u-lg-size1of2'
          }
          data-testid={testId ? `${testId}-card-right` : undefined}
        >
          {iconRight && (
            <BrandIcon
              icon={iconRight}
              color={iconColorRight}
              fill={fillRight ? true : false}
              stroke={strokeRight ? true : false}
              testId={testId ? `${testId}-icon-right` : undefined}
            />
          )}
          {headingRight && (
            <div
              className={`u-paddingTop20 u-paddingBottom8 ${
                headingRightColor ? headingColorMap[headingRightColor] : ''
              }`}
              dangerouslySetInnerHTML={{ __html: xss(headingRight) }}
              data-testid={testId ? `${testId}-heading-right` : null}
            />
          )}
          {contentRight && (
            <div
              className="u-paddingBottom32"
              dangerouslySetInnerHTML={{
                __html: addDataAnalyticsToRteLinks(
                  rteContentFilter(contentRight)
                )
              }}
              data-testid={testId ? `${testId}-content-right` : null}
            />
          )}
          {linkTextRight && (
            <a
              className="Button Button--secondary Button--secondary--action-blue"
              href={`${linkUrlRight}`}
              target={linkTargetRight}
              {...atb.dataAnalyticAttribute(
                linkAnalyticsRight?.trim() || 'item-click'
              )}
            >
              {linkTextRight}
            </a>
          )}
        </div>
      </div>
    </div>
  )
}
